// Telegram Bot Service for Order Notifications

const TELEGRAM_BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;

/**
 * Send a notification to Telegram
 * @param {string} message - The message to send
 * @returns {Promise<boolean>} - Success status
 */
export const sendTelegramNotification = async (message) => {
  if (!TELEGRAM_BOT_TOKEN || !TELEGRAM_CHAT_ID) {
    console.warn('Telegram credentials not configured. Skipping notification.');
    return false;
  }

  try {
    const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: TELEGRAM_CHAT_ID,
        text: message,
        parse_mode: 'HTML',
      }),
    });

    const data = await response.json();
    
    if (!data.ok) {
      console.error('Telegram API error:', data);
      return false;
    }

    console.log('Telegram notification sent successfully');
    return true;
  } catch (error) {
    console.error('Error sending Telegram notification:', error);
    return false;
  }
};

/**
 * Format and send a regular order notification
 * @param {Object} orderData - The order data
 * @param {string} orderId - The order ID
 */
export const notifyNewOrder = async (orderData, orderId) => {
  const items = orderData.items || [];
  const itemsList = items.map(item => {
    const itemName = item.displayName || item.name;
    const variantInfo = item.variant ? ` (${item.variant.name})` : '';
    return `  • ${itemName}${variantInfo} (${item.quantity}x) - ₹${(item.price * item.quantity).toFixed(2)}`;
  }).join('\n');

  const shipping = orderData.shippingInfo || {};
  const addressLine = `${shipping.address || ''}${shipping.landmark ? ', Near ' + shipping.landmark : ''}`;
  const cityLine = `${shipping.city || ''}, ${shipping.state || ''} - ${shipping.pincode || ''}`;

  const message = `
🛒 <b>NEW ORDER RECEIVED!</b>

📋 <b>Order ID:</b> ${orderId}
👤 <b>Customer:</b> ${shipping.fullName || orderData.userName || 'Guest'}
📧 <b>Email:</b> ${shipping.email || orderData.userEmail}
📱 <b>Phone:</b> ${shipping.phone || 'Not provided'}

📦 <b>Items:</b>
${itemsList}

💰 <b>Subtotal:</b> ₹${(orderData.subtotal || 0).toFixed(2)}
💰 <b>Tax (GST 18%):</b> ₹${(orderData.totalTax || 0).toFixed(2)}
${orderData.discount > 0 ? `🎉 <b>Discount:</b> -₹${orderData.discount.toFixed(2)} ${orderData.couponCode ? `(${orderData.couponCode})` : ''}\n` : ''}💰 <b>Total Amount:</b> ₹${(orderData.total || 0).toFixed(2)}

💳 <b>Payment:</b> ${orderData.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
${orderData.paymentStatus === 'paid' ? `✅ <b>Payment Status:</b> PAID (ID: ${orderData.paymentId})\n` : ''}
📍 <b>Delivery Address:</b>
${addressLine}
${cityLine}

⏰ <b>Order Time:</b> ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
  `.trim();

  return await sendTelegramNotification(message);
};

/**
 * Format and send a custom order notification
 * @param {Object} orderData - The custom order data
 * @param {string} orderId - The order ID
 */
export const notifyCustomOrder = async (orderData, orderId) => {
  const message = `
🎉 <b>NEW CUSTOM ORDER REQUEST!</b>

📋 <b>Order ID:</b> ${orderId}
👤 <b>Customer:</b> ${orderData.customerName}
📧 <b>Email:</b> ${orderData.email}
📱 <b>Phone:</b> ${orderData.phone}

🎊 <b>Event Type:</b> ${orderData.eventType}
📅 <b>Event Date:</b> ${orderData.eventDate} at ${orderData.eventTime}
👥 <b>Guest Count:</b> ${orderData.guestCount}
📍 <b>Venue:</b> ${orderData.venue}

📝 <b>Description:</b>
${orderData.eventDescription}

${orderData.specialRequirements ? `⚠️ <b>Special Requirements:</b>\n${orderData.specialRequirements}\n` : ''}
${orderData.budget ? `💰 <b>Budget:</b> ${orderData.budget}\n` : ''}

⏰ <b>Submitted:</b> ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
  `.trim();

  return await sendTelegramNotification(message);
};

/**
 * Send a test notification
 */
export const sendTestNotification = async () => {
  const message = `
✅ <b>Telegram Bot Connected!</b>

Your MD Sweets order notification system is working correctly.

You will receive notifications here for:
• Regular orders
• Custom order requests

⏰ ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
  `.trim();

  return await sendTelegramNotification(message);
};
